const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001'

export class ApiError extends Error {
  constructor(status, body) {
    super(body?.error || body?.message || `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

let accessToken = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('aidey_at')) || null
let activeWorkspaceId = null

export function setActiveWorkspaceId(id) { activeWorkspaceId = id }
export function getActiveWorkspaceId() { return activeWorkspaceId }

export function setAccessToken(t) {
  accessToken = t
  if (typeof sessionStorage !== 'undefined') {
    if (t) {
      sessionStorage.setItem('aidey_at', t)
      // Durable hint that a session exists — survives page refresh (unlike sessionStorage).
      // Used by refreshAccessToken() to avoid spurious refresh calls for users who
      // have never logged in, without relying on document.cookie (httpOnly cookies
      // are invisible to JS).
      if (typeof localStorage !== 'undefined') localStorage.setItem('aidey_session_hint', '1')
    } else {
      sessionStorage.removeItem('aidey_at')
      if (typeof localStorage !== 'undefined') localStorage.removeItem('aidey_session_hint')
    }
  }
}
export function getAccessToken() { return accessToken }

// Stable device fingerprint — persists in localStorage so the same browser
// always sends the same fingerprint, enabling session deduplication on the server.
function getDeviceFingerprint() {
  if (typeof localStorage === 'undefined') return null
  let fp = localStorage.getItem('aidey_device_fp')
  if (!fp) {
    fp = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
    localStorage.setItem('aidey_device_fp', fp)
  }
  return fp
}

// Single shared promise — all concurrent 401s wait on one refresh call instead of
// each firing independently and racing to rotate the refresh token.
let refreshPromise = null

export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise

  // Capture whether a session was believed to exist before this call — used to
  // gate aidey:auth:expired so anonymous visitors don't trigger logout side
  // effects when their boot-time refresh returns 401.
  // Kept for event-gating even though it no longer short-circuits the call, so
  // every browser with a valid httpOnly cookie gets a recovery attempt.
  const hadSessionHint =
    !!accessToken ||
    (typeof localStorage !== 'undefined' && !!localStorage.getItem('aidey_session_hint'))

  async function doRefresh(isRetry) {
    try {
      const res = await fetch(`${BASE}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Device-Fingerprint': getDeviceFingerprint() || '' },
      })
      if (res.ok) {
        const data = await res.json()
        setAccessToken(data.accessToken)
        return data
      }
      setAccessToken(null)
      if (hadSessionHint && typeof window !== 'undefined') {
        window.dispatchEvent(new Event('aidey:auth:expired'))
      }
      return false
    } catch (_) {
      if (!isRetry) {
        await new Promise(r => setTimeout(r, 1500))
        return doRefresh(true)
      }
      return 'network-error'
    }
  }

  refreshPromise = doRefresh(false).finally(() => { refreshPromise = null })
  return refreshPromise
}

export async function api(path, options = {}) {
  const fp = getDeviceFingerprint()
  const headers = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(fp ? { 'X-Device-Fingerprint': fp } : {}),
    ...(activeWorkspaceId ? { 'X-Active-Workspace': activeWorkspaceId } : {}),
    ...options.headers,
  }

  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  })

  if (res.status === 401 && path !== '/api/auth/refresh') {
    const refreshed = await refreshAccessToken()
    if (refreshed && refreshed !== 'network-error') {
      return api(path, options)
    }
    // Session expired — auth handler already fired aidey:auth:expired; mark
    // this error so the global unhandledrejection handler skips the toast.
    const body401 = await res.json().catch(() => ({}))
    const authErr = new ApiError(401, body401)
    authErr.isAuthError = true
    throw authErr
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    if (res.status === 403 && body?.code === 'PERSONAL_SUBSCRIPTION_REQUIRED') {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('aidey:subscription:required'))
      }
    }
    // ACCOUNT-4: access enforcement. A blocked user gets 402 on every non-exempt
    // route. Refresh currentUser (via the exempt /refresh path) so its enforcing
    // verdict flips and the paywall renders; mark the error handled so the global
    // rejection handler doesn't also surface a toast.
    if (res.status === 402 && body?.code === 'ACCESS_BLOCKED') {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('aidey:access:blocked', { detail: { reason: body?.reason || null } }))
      }
      const blockedErr = new ApiError(402, body)
      blockedErr.isAccessBlocked = true
      throw blockedErr
    }
    throw new ApiError(res.status, body)
  }

  return res
}
